/* components/reports/InventoryAnalysisReport.jsx — единый рендерер отчёта
   «Анализ инвентаризации» v2 (Report.result: _schema:'f40', schema_version:2).
   Используется и детальной страницей (/reports/:id), и инлайн-канвасом чата
   (InventoryCanvas) — раньше разметка дублировалась в двух местах.

   Структура зеркалит трёхшаговую методику:
     Шаг 1 — валидация (файлы, месяцы, сопоставимость, цели к выручке);
     Шаг 2 — ТОП-20 недостач/излишков с фактом/расчётом, Δ к прошлому и трендом;
     Шаг 3 — диагностика по 13 критериям, сводка причин, план, прогноз эффекта.

   Теги критериев КЛИКАБЕЛЬНЫ: клик по тегу (в сводке причин или в строке)
   фильтрует таблицу диагностики по критерию; повторный клик снимает фильтр.
   compact-режим — для канваса в чате (меньше колонок, без панели файлов). */

import React from 'react';
import { Pill } from '../ui/Badge.jsx';
import { Icon } from '../ui/Icon.jsx';
import { AiSkippedNote } from './AiSkippedNote.jsx';
import { fmt } from '../../lib/format.js';
import {
  INV_DIAG_STATUS, INV_TREND, critLabel, critTitle,
} from '../../lib/reports/inventoryCriteria.js';

const { useMemo, useState } = React;

const money0 = (v) => (v == null ? '—' : fmt.money(v));

/* Кликабельный тег критерия (фильтр диагностики). */
function CritChip({ id, active, onToggle, count, impact }) {
  return (
    <button
      type="button"
      className={'cv-critchip' + (active ? ' is-active' : '')}
      title={critTitle(id)}
      aria-pressed={active}
      onClick={onToggle}
    >
      {critLabel(id)}
      {count != null && <span className="num cv-critchip__n">{count}</span>}
      {count != null && impact != null && <span className="cv-critchip__sep" aria-hidden="true">·</span>}
      {impact != null && <span className="cv-critchip__impact num">{money0(impact)}</span>}
    </button>
  );
}

function CritChips({ ids = [], activeId, onToggle }) {
  if (!ids.length) return null;
  return (
    <span className="cv-rowchips">
      {ids.map((c) => (
        <CritChip key={c} id={c} active={activeId === c} onToggle={() => onToggle(c)} />
      ))}
    </span>
  );
}

function TrendBadge({ trend }) {
  const t = INV_TREND[trend] || INV_TREND.flat;
  return (
    <span className={'inv2-trend inv2-trend--' + t.tone} title={'К прошлому месяцу: ' + t.label}>
      <span aria-hidden="true">{t.glyph}</span> {t.label}
    </span>
  );
}

/* Пара «кол-во · сумма» (факт/расчёт). */
function QtyRub({ qty, unit, rub }) {
  if (qty == null && rub == null) return <span className="text-mute">—</span>;
  return (
    <span className="num inv2-pair">
      {qty != null ? `${qty} ${unit || ''}`.trim() : '—'}
      <span className="text-mute"> · {money0(rub)}</span>
    </span>
  );
}

function Sec({ title, hint, children }) {
  return (
    <section className="canvas-sec">
      {title && (
        <div className="canvas-sec__head">
          <h4 className="canvas-sec__title t-eyebrow">{title}</h4>
          {hint && <span className="canvas-sec__hint t-caption text-mute">{hint}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ── Шаг 1: панель валидации ── */
function ValidationPanel({ v, compact }) {
  if (!v) return null;
  const months = (v.months || []).filter(Boolean);
  const issues = v.issues || [];
  return (
    <Sec title="Валидация данных">
      <div className="inv2-valid">
        <div className="inv2-valid__row">
          {months.length > 0 && (
            <span className="inv2-valid__fact">
              <Icon name="calendar" size={13} /> {months.join(' → ')}
            </span>
          )}
          {v.adjacent_months != null && (
            <Pill tone={v.adjacent_months ? 'ok' : 'warn'} size="sm">
              {v.adjacent_months ? 'соседние месяцы' : 'месяцы не соседние'}
            </Pill>
          )}
          {v.join_coverage_pct != null && (
            <span className="inv2-valid__fact" title="Доля позиций последнего месяца, найденных в прошлом">
              <Icon name="link" size={13} /> сопоставлено {fmt.percent(v.join_coverage_pct)}
            </span>
          )}
          {(v.units || []).length > 0 && (
            <span className="inv2-valid__fact text-mute">ед.: {(v.units || []).join(', ')}</span>
          )}
        </div>
        {!compact && (v.files || []).map((f, i) => (
          <div className="inv2-file" key={i}>
            <Icon name="file" size={14} />
            <span className="cv-strong truncate">{f.filename || 'файл'}</span>
            <span className="text-mute t-caption">
              {[f.period_label, f.doc_date && fmt.date(f.doc_date),
                f.rows_parsed != null ? `${f.rows_parsed} строк` : null,
                f.parse_method === 'llm' ? 'распознано ИИ' : null]
                .filter(Boolean).join(' · ')}
            </span>
            {f.totals_crosscheck_ok === true && <Pill tone="ok" size="sm">итоги сходятся</Pill>}
            {f.totals_crosscheck_ok === false && <Pill tone="warn" size="sm">итоги не сходятся</Pill>}
          </div>
        ))}
        {issues.length > 0 && (
          <div className="callout callout--warn inv2-callout" role="note">
            <Icon name="alert-triangle" size={15} />
            <div>{issues.map((s, i) => <div key={i}>{s}</div>)}</div>
          </div>
        )}
      </div>
    </Sec>
  );
}

/* ── Шаг 2: ТОП-таблица недостач/излишков ── */
function TopTable({ rows = [], compact, activeCrit, onToggleCrit }) {
  if (!rows.length) return <p className="t-caption text-mute">Нет позиций.</p>;
  return (
    <div className="cv-table-wrap">
      <table className="cv-table inv2-table">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Товар</th>
            <th className="num">Δ, кол-во</th>
            <th className="num">Δ, ₽</th>
            {!compact && <th className="num">Факт</th>}
            {!compact && <th className="num">Расчёт</th>}
            <th className="num">Прошлый мес., Δ₽</th>
            <th>Тренд</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.n} className={activeCrit && !(r.flags || []).includes(activeCrit) ? 'inv2-row--dim' : ''}>
              <td className="num text-mute" data-label="#">{r.n}</td>
              <td data-label="Товар">
                <span className="cv-strong">{r.name}</span>
                <CritChips ids={r.flags || []} activeId={activeCrit} onToggle={onToggleCrit} />
              </td>
              <td className="num" data-label="Δ, кол-во">{r.delta_qty} {r.unit}</td>
              <td className="num" data-label="Δ, ₽">
                <span className={'inv2-delta ' + (r.delta_rub < 0 ? 'inv2-delta--bad' : 'inv2-delta--info')}>
                  {fmt.money(r.delta_rub, { sign: true })}
                </span>
              </td>
              {!compact && (
                <td className="num" data-label="Факт">
                  <QtyRub qty={r.fact_qty} unit={r.unit} rub={r.fact_rub} />
                </td>
              )}
              {!compact && (
                <td className="num" data-label="Расчёт">
                  <QtyRub qty={r.calc_qty} unit={r.unit} rub={r.calc_rub} />
                </td>
              )}
              <td className="num" data-label="Прошлый мес., Δ₽">
                {r.prev_delta_rub == null
                  ? <span className="text-mute">—</span>
                  : fmt.money(r.prev_delta_rub, { sign: true })}
              </td>
              <td data-label="Тренд"><TrendBadge trend={r.trend} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Шаг 3: диагностика ── */
function DiagnosticsTable({ rows = [], compact, activeCrit, onToggleCrit }) {
  const shown = activeCrit
    ? rows.filter((d) => (d.criteria || []).includes(activeCrit)
      || (d.machine_flags || []).includes(activeCrit))
    : rows;
  if (!rows.length) {
    return <p className="t-caption text-mute">Диагностика недоступна — построена только расчётная часть.</p>;
  }
  return (
    <div className="cv-table-wrap">
      {activeCrit && (
        <p className="t-caption text-mute inv2-filternote">
          Фильтр: «{critLabel(activeCrit)}» — {shown.length} из {rows.length}.{' '}
          <button type="button" className="inv2-reset" onClick={() => onToggleCrit(activeCrit)}>Сбросить</button>
        </p>
      )}
      <table className="cv-table inv2-table">
        <thead>
          <tr>
            <th>Товар</th>
            {!compact && <th>Диагноз</th>}
            <th>Рекомендация</th>
            <th className="num">Эффект, ₽</th>
            {!compact && <th>Данные</th>}
            <th>Статус</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((d, i) => {
            const st = INV_DIAG_STATUS[d.status] || INV_DIAG_STATUS.awaiting_data;
            return (
              <tr key={d.row_ref != null ? d.row_ref : i}>
                <td data-label="Товар">
                  <span className="cv-strong">{d.name}</span>
                  <span className="num text-mute t-caption inv2-deltahint">
                    {fmt.money(d.delta_rub, { sign: true })}
                  </span>
                  <CritChips ids={d.criteria || []} activeId={activeCrit} onToggle={onToggleCrit} />
                </td>
                {!compact && (
                  <td data-label="Диагноз" className="cv-comment"><span className="text-dim">{d.diagnosis || '—'}</span></td>
                )}
                <td data-label="Рекомендация" className="cv-comment"><span className="text-dim">{d.recommendation || '—'}</span></td>
                <td className="num" data-label="Эффект, ₽">
                  {d.potential_effect_rub != null
                    ? <span className="inv2-delta inv2-delta--ok">{money0(d.potential_effect_rub)}</span>
                    : <span className="text-mute">—</span>}
                </td>
                {!compact && (
                  <td data-label="Данные" className="text-mute">{(d.data_needed || []).join('; ') || '—'}</td>
                )}
                <td data-label="Статус"><Pill tone={st.tone} size="sm">{st.label}</Pill></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── прогнозный эффект (сценарии) ── */
function ProjectedEffect({ pe, revenueLast, targets }) {
  if (!pe) return null;
  const cur = pe.current || {};
  const card = (s, label, pick) => {
    if (!s) return null;
    return (
      <div className={'cv-rec' + (pick ? ' cv-rec--pick' : '')} key={label}>
        <span className="cv-rec__label">{label}</span>
        <span className="cv-rec__save num">недостача {money0(s.shortage_rub != null ? s.shortage_rub : s.shortage_after_rub)}</span>
        <span className="cv-rec__meas t-caption text-mute">
          излишки {money0(s.surplus_rub != null ? s.surplus_rub : s.surplus_after_rub)}
          {s.recovered_rub != null && <> · возврат {money0(s.recovered_rub)}</>}
          {s.deviation_pct_of_revenue != null && <> · откл. {fmt.percent(s.deviation_pct_of_revenue, { digits: 1 })} выручки</>}
        </span>
      </div>
    );
  };
  return (
    <Sec title="Прогнозный эффект" hint={revenueLast ? `от выручки ${money0(revenueLast)}` : 'выручка не указана — % недоступен'}>
      <div className="cv-recs">
        {card(cur, 'Сейчас', false)}
        {card(pe.conservative, 'Консервативный', false)}
        {card(pe.target, 'Целевой', true)}
      </div>
      {pe.method && <p className="t-caption text-mute inv2-method">Метод оценки: {pe.method}</p>}
      {targets && (
        <p className="t-caption text-mute inv2-method">
          Цели: недостача ≤ {targets.shortage_max_pct}% выручки, излишек ≤ {targets.surplus_max_pct}%.
        </p>
      )}
    </Sec>
  );
}

function InventoryAnalysisReport({ r, compact = false }) {
  const [activeCrit, setActiveCrit] = useState(null);
  const toggleCrit = (id) => setActiveCrit((cur) => (cur === id ? null : id));

  const totals = r.totals || {};
  const last = totals.last || {};
  const revenue = r.revenue || [null, null];
  const months = ((r.validation || {}).months || []).filter(Boolean);

  const metricItems = useMemo(() => {
    const sh = totals.shortage_pct_of_revenue;
    const su = totals.surplus_pct_of_revenue;
    // Период и выручка опциональны (период — из дат ведомостей, выручка — из
    // текста пользователя): пустую плитку с «—» не показываем — только шум.
    const items = [];
    if (months.length) {
      items.push({ label: 'Период', value: months[months.length - 1] });
    }
    items.push(
      {
        label: 'Недостача' + (sh != null ? ` · ${fmt.percent(sh, { digits: 1 })} выручки` : ''),
        value: money0(last.shortage_rub),
        tone: totals.shortage_target_met === false ? 'bad' : (totals.shortage_target_met ? 'ok' : 'bad'),
      },
      {
        label: 'Излишки' + (su != null ? ` · ${fmt.percent(su, { digits: 1 })} выручки` : ''),
        value: money0(last.surplus_rub),
        tone: totals.surplus_target_met === false ? 'warn' : 'info',
      },
    );
    if (revenue[1] != null) {
      items.push({ label: 'Выручка месяца', value: money0(revenue[1]) });
    }
    return items;
  }, [totals, last, months, revenue]);

  const causes = r.cause_summary || [];
  const missort = r.missort_pairs || [];

  return (
    <>
      {/* метрики — как CanvasMetrics, но label несёт % к цели */}
      <div className="canvas-metrics" style={{ '--cm-count': metricItems.length }}>
        {metricItems.map((m, i) => (
          <div className={'canvas-metric' + (m.tone ? ' canvas-metric--' + m.tone : '')} key={i}>
            <span className="canvas-metric__val num">{m.value}</span>
            <span className="canvas-metric__lbl t-caption">{m.label}</span>
          </div>
        ))}
      </div>

      <AiSkippedNote r={r} />
      <ValidationPanel v={r.validation} compact={compact} />

      {causes.length > 0 && (
        <Sec title="Причины расхождений" hint="клик по тегу фильтрует диагностику">
          <div className="cv-chips">
            {causes.map((c) => (
              <CritChip
                key={c.criterion} id={c.criterion}
                active={activeCrit === c.criterion}
                onToggle={() => toggleCrit(c.criterion)}
                count={c.items} impact={compact ? null : c.impact_rub}
              />
            ))}
          </div>
        </Sec>
      )}

      <Sec title={`ТОП недостач (${(r.top_shortages || []).length})`}>
        <TopTable rows={r.top_shortages} compact={compact} activeCrit={activeCrit} onToggleCrit={toggleCrit} />
      </Sec>
      <Sec title={`ТОП излишков (${(r.top_surpluses || []).length})`}>
        <TopTable rows={r.top_surpluses} compact={compact} activeCrit={activeCrit} onToggleCrit={toggleCrit} />
      </Sec>

      {!compact && missort.length > 0 && (
        <Sec title="Кандидаты пересорта" hint="взаимозачёт = min(|Δ|) пары">
          <ul className="cv-note-list">
            {missort.map((p, i) => (
              <li key={i}>
                <Icon name="link" size={13} />
                <span className="cv-strong">{p.shortage_item}</span>
                <span className="text-mute"> ⇄ </span>
                <span className="cv-strong">{p.surplus_item}</span>
                <span className="text-mute num"> · компенсация {money0(p.compensation_rub)}</span>
                {p.confidence != null && (
                  <span className="text-mute num" title="Уверенность пары: похожесть имён + близость переброшенного количества">
                    {' · уверенность '}{Math.round(p.confidence * 100)}%
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Sec>
      )}

      <Sec title="Диагностика по 13 критериям" hint={compact ? null : 'теги кликабельны'}>
        <DiagnosticsTable rows={r.diagnostics} compact={compact} activeCrit={activeCrit} onToggleCrit={toggleCrit} />
      </Sec>

      {(r.action_plan || []).length > 0 && (
        <Sec title="План действий">
          <div className="cv-table-wrap">
            <table className="cv-table inv2-table">
              <thead>
                <tr><th className="num">#</th><th>Действие</th><th className="num">Эффект, ₽</th>{!compact && <th>Срок</th>}{!compact && <th>Ответственный</th>}</tr>
              </thead>
              <tbody>
                {r.action_plan.map((a) => (
                  <tr key={a.priority}>
                    <td className="num text-mute" data-label="#">{a.priority}</td>
                    <td data-label="Действие">{a.action}{a.data_needed && <span className="text-mute t-caption"> · {a.data_needed}</span>}</td>
                    <td className="num" data-label="Эффект, ₽">{a.expected_effect_rub != null ? <span className="inv2-delta inv2-delta--ok">{money0(a.expected_effect_rub)}</span> : <span className="text-mute">—</span>}</td>
                    {!compact && <td data-label="Срок" className="text-mute">{a.term || '—'}</td>}
                    {!compact && <td data-label="Ответственный" className="text-mute">{a.owner || '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>
      )}

      <ProjectedEffect pe={r.projected_effect} revenueLast={revenue[1]} targets={r.targets} />

      {(r.data_requests || []).length > 0 && (
        <Sec title="Что прислать для уточнения">
          <ul className="cv-note-list">
            {r.data_requests.map((s, i) => (
              <li key={i}><Icon name="file" size={13} /><span className="text-dim">{s}</span></li>
            ))}
          </ul>
        </Sec>
      )}

      {(r.clarifications || []).length > 0 && (
        <div className="callout callout--info inv2-callout" role="note">
          <Icon name="info" size={15} />
          <div>{r.clarifications.map((s, i) => <div key={i}>{s}</div>)}</div>
        </div>
      )}

      {r.summary && (
        <div className="cv-callout report-verdict"><Icon name="sparkles" size={18} /><p>{r.summary}</p></div>
      )}
    </>
  );
}

export { InventoryAnalysisReport };
